import { toast } from "react-toastify";
import { EditOutlined, ReloadOutlined, SyncOutlined, UndoOutlined } from "@ant-design/icons";
import { Box, IconButton, InputAdornment } from "@mui/material";
import { Form, Modal, Popconfirm, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BillingService } from "../../../api/billingService";
import { ExchangeRate } from "../../../@type/billing.types";
import { PERM } from "../../../auth/permissions";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import Card from "../../../components/common/Card";
import InputField from "../../../components/common/fields/InputField";
import MuiTable from "../../../components/common/MuiTable";
import StatusBadge from "../../../components/common/StatusBadge";
import { useMe } from "../../../auth/MeContext";
import { Column } from "../../../utils/constants";

const unwrap = (response: any) => response?.data?.data ?? response?.data ?? [];

const formatRate = (value: number) =>
  Number(value).toLocaleString("en-US", { maximumFractionDigits: 8 });

const ExchangeRateConfig = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ rate: number }>();
  const { can } = useMe();
  const canUpdate = can(PERM.EXCHANGE_RATE_UPDATE);
  const [rates, setRates] = useState<ExchangeRate[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<ExchangeRate | null>(null);

  const loadRates = useCallback(async () => {
    setLoading(true);
    try {
      setRates(unwrap(await BillingService.getExchangeRates()));
    } catch (error) {
      toast.error(t("billing.rate.load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadRates();
  }, [loadRates]);

  const baseCurrency = rates[0]?.base_currency || "USD";

  const filtered = useMemo(() => {
    const keyword = search.trim().toUpperCase();
    return keyword ? rates.filter((rate) => rate.currency.includes(keyword)) : rates;
  }, [rates, search]);

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      setRates(unwrap(await BillingService.refreshExchangeRates()));
      toast.success(t("billing.rate.refreshed"));
    } catch (error) {
      toast.error(t("billing.rate.refresh_error"));
    } finally {
      setRefreshing(false);
    }
  };

  const openEdit = (rate: ExchangeRate) => {
    form.setFieldsValue({ rate: rate.rate });
    setEditing(rate);
  };

  const saveManual = async () => {
    if (!editing) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      await BillingService.setManualExchangeRate(editing.currency, Number(values.rate));
      toast.success(t("billing.rate.overridden", { currency: editing.currency }));
      setEditing(null);
      await loadRates();
    } catch (error: any) {
      if (!error?.errorFields) {
        toast.error(t("billing.rate.save_error"));
      }
    } finally {
      setSaving(false);
    }
  };

  const resetToAuto = async (rate: ExchangeRate) => {
    try {
      await BillingService.resetExchangeRate(rate.currency);
      toast.success(t("billing.rate.back_auto", { currency: rate.currency }));
      await loadRates();
    } catch (error) {
      toast.error(t("billing.rate.reset_error"));
    }
  };

  const header: Column[] = [
    {
      id: "currency",
      label: t("billing.rate.col.currency"),
      minWidth: 100,
      align: "left",
      format: (value: string) => <span className="font-semibold">{value}</span>,
    },
    {
      id: "rate",
      label: t("billing.rate.col.rate", { base: baseCurrency }),
      minWidth: 160,
      align: "right",
      format: (value: number) => formatRate(value),
    },
    {
      id: "source",
      label: t("billing.rate.col.source"),
      minWidth: 110,
      align: "center",
      format: (value: string) => (
        <StatusBadge
          label={value === "manual" ? t("billing.rate.manual") : t("billing.rate.auto")}
          tone={value === "manual" ? "warning" : "success"}
        />
      ),
    },
    {
      id: "fetched_at",
      label: t("billing.rate.col.updated"),
      minWidth: 170,
      align: "center",
      format: (value: string) => (value ? value.replace("T", " ").slice(0, 19) : ""),
    },
    ...(canUpdate
      ? [
          {
            id: "actions",
            label: t("billing.rate.col.actions"),
            minWidth: 110,
            align: "center",
            format: (_: unknown, row: ExchangeRate) => (
              <Space size={4}>
                <IconButton size="small" color="success" onClick={() => openEdit(row)}>
                  <EditOutlined />
                </IconButton>
                {row.source === "manual" && (
                  <Popconfirm
                    title={t("billing.rate.reset_title")}
                    description={t("billing.rate.reset_desc")}
                    onConfirm={() => resetToAuto(row)}
                  >
                    <IconButton size="small" color="warning">
                      <UndoOutlined />
                    </IconButton>
                  </Popconfirm>
                )}
              </Space>
            ),
          } as Column,
        ]
      : []),
  ];

  return (
    <Card className="user">
      <Space direction="vertical" size={20} style={{ width: "100%" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {t("billing.rate.title")}
            </Typography.Title>
            <Typography.Text type="secondary">
              {t("billing.rate.subtitle")}
            </Typography.Text>
          </div>
          <Space>
            <InputField
              placeholder={t("billing.rate.search_ph")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              endAdornment={<InputAdornment position="end">⌕</InputAdornment>}
            />
            <CancelButton startIcon={<ReloadOutlined />} onClick={loadRates} loading={loading}>
              {t("billing.rate.reload")}
            </CancelButton>
            {canUpdate && (
              <ActionButton startIcon={<SyncOutlined />} onClick={refreshAll} loading={refreshing}>
                {t("billing.rate.fetch_latest")}
              </ActionButton>
            )}
          </Space>
        </Box>

        <MuiTable header={header} data={filtered} isLoading={loading} />
      </Space>

      <Modal
        title={editing ? t("billing.rate.override_title", { currency: editing.currency }) : ""}
        open={Boolean(editing)}
        onCancel={() => setEditing(null)}
        footer={
          <Box display="flex" justifyContent="flex-end" gap={1.5}>
            <CancelButton onClick={() => setEditing(null)}>{t("btn.cancel")}</CancelButton>
            <ActionButton onClick={saveManual} loading={saving}>
              {t("billing.rate.save")}
            </ActionButton>
          </Box>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="rate"
            label={`1 ${baseCurrency} = ? ${editing?.currency ?? ""}`}
            rules={[
              { required: true, message: t("billing.rate.required") },
              {
                validator: (_, value) =>
                  Number(value) > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error(t("billing.rate.gt_zero"))),
              },
            ]}
          >
            <InputField
              type="number"
              inputProps={{ min: 0, step: "any" }}
              endAdornment={<InputAdornment position="end">{editing?.currency}</InputAdornment>}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ExchangeRateConfig;
